import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Select,
  DatePicker,
  message,
  Spin,
  Switch,
} from "antd";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
  getCurrentFinancialYear,
  getFinancialYearOptions,
} from "../utils/financialYearUtils";
import useLatestRequest from "../utils/useLatestRequest";
import {
  REPORT_DATE_FORMAT,
  parseStoredDateRange,
  serializeDateRange,
  formatReportDate,
} from "../utils/reportDateRangeUtils";

const resolveSalesOrderId = (row = {}) => {
  if (row.soId) return row.soId;
  if (row.salesOrderId) {
    return typeof row.salesOrderId === "object"
      ? row.salesOrderId._id || row.salesOrderId.id
      : row.salesOrderId;
  }
  if (row._id) return row._id;
  if (row.id) return row.id;
  return null;
};

const { RangePicker } = DatePicker;
const { Option } = Select;

const FILTERS_STORAGE_KEY = "report:salesman-register:filters";

const components = {
  header: {
    cell: (props) => (
      <th
        {...props}
        style={{
          ...props.style,
          backgroundColor: "#daeffe",
        }}
      />
    ),
  },
};

const SalesmanWiseReport = ({ onBack }) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());
  const [downloading, setDownloading] = useState(false);
  const [partyOptions, setPartyOptions] = useState([]);
  const [partyName, setPartyName] = useState(undefined);
  const [salesmanOptions, setSalesmanOptions] = useState([]);
  const [selectedSalesman, setSelectedSalesman] = useState(undefined);
  const [propertyGroupOptions, setPropertyGroupOptions] = useState([]);
  const [selectedPropertyGroup, setSelectedPropertyGroup] = useState(undefined);
  const [isSummary, setIsSummary] = useState(false);
  const [grandTotal, setGrandTotal] = useState(null);
  const [filtersHydrated, setFiltersHydrated] = useState(false);

  // Restore filters on mount (Back navigation / refresh).
  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem(FILTERS_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return;

      if (typeof parsed.financialYear === "string" && parsed.financialYear) {
        setFinancialYear(parsed.financialYear);
      }
      if (typeof parsed.partyName === "string") {
        setPartyName(parsed.partyName || undefined);
      }
      if (typeof parsed.selectedSalesman === "string") {
        setSelectedSalesman(parsed.selectedSalesman || undefined);
      }
      if (typeof parsed.selectedPropertyGroup === "string") {
        setSelectedPropertyGroup(parsed.selectedPropertyGroup || undefined);
      }
      if (typeof parsed.isSummary === "boolean") {
        setIsSummary(parsed.isSummary);
      }
      const restoredDateRange = parseStoredDateRange(parsed.dateRange);
      if (restoredDateRange) setDateRange(restoredDateRange);
    } catch (e) {
      // Ignore bad JSON / storage issues
    } finally {
      setFiltersHydrated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist filters whenever they change.
  useEffect(() => {
    if (!filtersHydrated) return;
    try {
      const payload = {
        financialYear,
        partyName: partyName || "",
        selectedSalesman: selectedSalesman || "",
        selectedPropertyGroup: selectedPropertyGroup || "",
        isSummary,
        dateRange: serializeDateRange(dateRange),
      };
      window.sessionStorage.setItem(
        FILTERS_STORAGE_KEY,
        JSON.stringify(payload),
      );
    } catch (e) {
      // Ignore quota / storage issues
    }
  }, [
    filtersHydrated,
    financialYear,
    partyName,
    selectedSalesman,
    selectedPropertyGroup,
    isSummary,
    dateRange,
  ]);

  useEffect(() => {
    // fetch salesmen for the new filter
    (async () => {
      try {
        const res = await fetch(
          `${apiBaseUrl}/inventoryManagement/listSalesPersons?page=1&limit=50`,
          {
            method: "GET",
            credentials: "include",
          },
        );
        const j = await res.json();
        if (j.success && Array.isArray(j.data)) {
          const seen = new Set();
          const opts = j.data
            .map((s) => String(s?.name ?? "").trim())
            .filter((name) => {
              if (!name) return false;
              if (seen.has(name)) return false;
              seen.add(name);
              return true;
            })
            .sort((a, b) => a.localeCompare(b))
            .map((name) => ({ label: name, value: name }));
          setSalesmanOptions(opts);
        }
      } catch (e) {
        console.error("Failed to fetch salesmen", e);
      }
    })();
  }, [apiBaseUrl]);


  const beginRequest = useLatestRequest();

  const fetchData = async () => {
    const isCurrent = beginRequest();
    setLoading(true);
    setGrandTotal(null);
    try {
      const params = new URLSearchParams();
      params.set("financialYear", financialYear);
      params.set("isSummary", isSummary ? "true" : "false");
      if (selectedSalesman) params.set("createdBy", selectedSalesman);
      if (partyName) params.set("partyName", partyName);
      const startDate = formatReportDate(dateRange?.[0]);
      const endDate = formatReportDate(dateRange?.[1]);
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      if (selectedPropertyGroup)
        params.set("propertyGroup", selectedPropertyGroup);

      const q = params.toString() ? `?${params.toString()}` : "";
      const res = await fetch(
        `${apiBaseUrl}/inventoryManagement/getSalesmanRegister${q}`,
        {
          method: "GET",
          credentials: "include",
        },
      );
      const j = await res.json();
      if (!isCurrent()) return;
      if (j.success) {
        setRows(
          (j.data || []).map((row) => ({
            ...row,
            soId: resolveSalesOrderId(row),
          })),
        );
        const fromTotals = j.grandTotals?.amount;
        const legacy = j.grandTotal;
        const gt =
          fromTotals != null && fromTotals !== ""
            ? Number(fromTotals)
            : legacy != null && legacy !== ""
              ? Number(legacy)
              : null;
        setGrandTotal(gt != null && !Number.isNaN(gt) ? gt : null);

        if (!isSummary) {
          const parties = Array.from(
            new Set((j.data || []).map((r) => r.partyName)),
          ).filter(Boolean);
          setPartyOptions(parties.map((p) => ({ label: p, value: p })));

          const groups = Array.from(
            new Set((j.data || []).map((r) => r.propertyGroup)),
          ).filter(Boolean);
          groups.sort((a, b) => String(a).localeCompare(String(b)));
          setPropertyGroupOptions(groups.map((g) => ({ label: g, value: g })));
        }
      } else {
        message.error("Failed to fetch report");
      }
    } catch (err) {
      console.error(err);
      if (isCurrent()) message.error("Error fetching report");
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };
  useEffect(() => {
    if (!filtersHydrated) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filtersHydrated,
    dateRange,
    partyName,
    selectedSalesman,
    selectedPropertyGroup,
    isSummary,
    financialYear,
  ]);

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      params.set("financialYear", financialYear);
      params.set("excel", "true");
      params.set("isSummary", isSummary ? "true" : "false");
      if (selectedSalesman) params.set("createdBy", selectedSalesman);
      if (partyName) params.set("partyName", partyName);
      const startDate = formatReportDate(dateRange?.[0]);
      const endDate = formatReportDate(dateRange?.[1]);
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      if (selectedPropertyGroup)
        params.set("propertyGroup", selectedPropertyGroup);
      const q = params.toString() ? `?${params.toString()}` : "";
      const url = `${apiBaseUrl}/inventoryManagement/getSalesmanRegister${q}`;

      const res = await fetch(url, { method: "GET", credentials: "include" });
      if (!res.ok) {
        message.error("Failed to download Excel");
        return;
      }
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = isSummary
        ? `Salesman_Register_Summary_${financialYear}_${moment().format("YYYY-MM-DD")}.xlsx`
        : `Salesman_Register_${financialYear}_${moment().format("YYYY-MM-DD")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error(err);
      message.error("Error downloading Excel");
    } finally {
      setDownloading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "SO No.",
        dataIndex: "soNo",
        key: "soNo",
        align: "center",
        width: 130,
        render: (v, record) => {
          const soId = record?.soId;
          if (!v || !soId) {
            return <span>{v || "-"}</span>;
          }

          return (
            <span
              role="link"
              tabIndex={0}
              style={{
                color: "#1677ff",
                cursor: "pointer",
                textDecoration: "underline",
              }}
              onClick={() => navigate(`/sales-order/${soId}?tab=1`)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(`/sales-order/${soId}?tab=1`);
                }
              }}
              title="Open Sales Order"
            >
              {v}
            </span>
          );
        },
      },
      {
        title: "Suffix",
        dataIndex: "suffix",
        key: "suffix",
        align: "center",
        width: 90,
        render: (v) => (v != null && v !== "" ? String(v) : "-"),
      },
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        align: "center",
        width: 110,
        render: (d) => (d != null && d !== "" ? String(d) : "-"),
      },
      {
        title: "Party",
        dataIndex: "partyName",
        key: "partyName",
        align: "left",
        width: 200,
      },
      {
        title: "Brand Name",
        dataIndex: "brandName",
        key: "brandName",
        align: "left",
        width: 150,
      },
      {
        title: "Brand Segment",
        dataIndex: "brandSegment",
        key: "brandSegment",
        align: "center",
        width: 120,
      },
      {
        title: "Property Group",
        dataIndex: "propertyGroup",
        key: "propertyGroup",
        align: "center",
        width: 150,
      },
      {
        title: "Property Name",
        dataIndex: "propertyName",
        key: "propertyName",
        align: "left",
        width: 180,
      },
      {
        title: "City",
        dataIndex: "propertyCity",
        key: "propertyCity",
        align: "center",
        width: 120,
      },
      {
        title: "Location",
        dataIndex: "location",
        key: "location",
        align: "left",
        width: 180,
      },
      {
        title: "Total Days",
        dataIndex: "noOfDays",
        key: "noOfDays",
        align: "center",
        width: 90,
      },
      {
        title: "From",
        dataIndex: "fromDate",
        key: "fromDate",
        align: "center",
        width: 110,
        render: (d) => (d != null && d !== "" ? String(d) : "-"),
      },
      {
        title: "To",
        dataIndex: "toDate",
        key: "toDate",
        align: "center",
        width: 110,
        render: (d) => (d != null && d !== "" ? String(d) : "-"),
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        align: "right",
        width: 120,
        render: (v) => (v ? new Intl.NumberFormat("en-IN").format(v) : "-"),
      },
      {
        title: "Salesman",
        dataIndex: "createdByName",
        key: "createdByName",
        align: "left",
        width: 150,
      },
    ],
    [navigate],
  );

  const formatAmount = (v) =>
    typeof v === "number"
      ? new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(v)
      : v ?? "-";

  const summaryColumns = [
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      width: 180,
      align: "center",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 140,
      align: "center",
      render: formatAmount,
    },
  ];

  return (
    <div
      className="parent-padding-container"
      style={{ backgroundColor: "white", borderRadius: 12, padding: 20 }}
    >
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 16,
              width: "100%",
            }}
          >
            <Space size="small" style={{ flexShrink: 0 }}>
              <Button icon={<FaArrowLeft />} onClick={onBack} type="text" />
              <span style={{ fontWeight: 600, fontSize: 16 }}>Salesman Register</span>
            </Space>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                padding: "12px 0",
                flexShrink: 0,
              }}
            >
              <Space wrap size="middle">
                <Switch
                  checkedChildren="Summary"
                  unCheckedChildren="Detailed"
                  checked={isSummary}
                  onChange={setIsSummary}
                />
                <Select
                  value={financialYear}
                  onChange={setFinancialYear}
                  style={{ width: 120 }}
                  placeholder="Select FY"
                >
                  {getFinancialYearOptions().map((fy) => (
                    <Option key={fy} value={fy}>
                      {fy}
                    </Option>
                  ))}
                </Select>
                <Select
                  placeholder="Filter by Salesman"
                  allowClear
                  showSearch
                  filterOption={(input, option) => {
                    const label = String(option?.label ?? "").toLowerCase();
                    const query = String(input ?? "").toLowerCase();
                    if (!query) return true;
                    return label.startsWith(query);
                  }}
                  value={selectedSalesman}
                  onChange={(val) => {
                    setSelectedSalesman(val);
                  }}
                  options={salesmanOptions}
                  style={{ width: 220 }}
                  disabled={salesmanOptions.length === 0}
                />
                <Select
                  placeholder="Property Group"
                  allowClear
                  showSearch
                  value={selectedPropertyGroup}
                  onChange={setSelectedPropertyGroup}
                  filterOption={(input, option) => {
                    const label = String(option?.label ?? "").toLowerCase();
                    const query = String(input ?? "").toLowerCase();
                    if (!query) return true;
                    return label.startsWith(query);
                  }}
                  options={propertyGroupOptions}
                  style={{ width: 200 }}
                />
              </Space>
              <Space wrap size="middle">
                <Select
                  placeholder="Party"
                  allowClear
                  showSearch
                  value={partyName}
                  onChange={setPartyName}
                  filterOption={(input, option) => {
                    const label = String(option?.label ?? "").toLowerCase();
                    const query = String(input ?? "").toLowerCase();
                    if (!query) return true;
                    return label.startsWith(query);
                  }}
                  options={partyOptions}
                  style={{ width: 240 }}
                />
                <RangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  format={REPORT_DATE_FORMAT}
                  style={{ width: 220 }}
                />
                <Button
                  type="primary"
                  icon={<FaDownload />}
                  loading={downloading}
                  disabled={loading}
                  onClick={handleDownloadExcel}
                >
                  Download Excel
                </Button>
              </Space>
            </div>
          </div>
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin />
          </div>
        ) : rows.length === 0 && grandTotal == null ? (
          <div style={{ textAlign: "center", padding: 60, color: "#999" }}>
            No records found
          </div>
        ) : isSummary ? (
          <Table
            components={components}
            columns={summaryColumns}
            dataSource={rows}
            rowKey={(r, i) => r.month || `summary-${i}`}
            pagination={false}
            scroll={{ x: "max-content" }}
            size="small"
            summary={() =>
              grandTotal != null ? (
                <Table.Summary>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} align="center">
                      <strong>Grand Total</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="center">
                      <strong>{formatAmount(grandTotal)}</strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null
            }
          />
        ) : (
          <Table
            components={components}
            columns={columns}
            dataSource={rows}
            rowKey={(r, i) =>
              `${r.soNo}-${r.propertyName || i}-${r.fromDate || i}`
            }
            pagination={false}
            scroll={{ y: 520 }}
            sticky
            summary={() =>
              grandTotal != null ? (
                <Table.Summary>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={13} align="center">
                      <strong>Grand Total</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={13} align="right">
                      <strong>{formatAmount(grandTotal)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={14} align="left" />
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null
            }
          />
        )}
      </Card>
    </div>
  );
};

export default SalesmanWiseReport;
